import { useHandoffs, useSubmissions as useExchangeSubmissions } from '../supplierExchange/hooks';
import { completedSubmissions } from '../supplierExchange/submissionGroups';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { PORTAL_TABS, tabFromParams } from './portalTabs';
import { Row, RowGroup } from '../../components/ui/RowGroup';
import type { PortalTab } from './portalTabs';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  InlineError,
  Input,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { usePasteImageUpload } from '../comments/pasteImage';
import { RichTextEditor, RichTextView, isRichTextEmpty } from '../../components/RichText';
import { useAuth } from '../auth/AuthProvider';
import { formatDate } from '../listView/TaskTable';
import {
  useAddSamplePhoto,
  useCreateReviewComment,
  useCreateSubmission,
  useDeleteReviewComment,
  useReviewComments,
  useReviewPeople,
  useReviewStatuses,
  useSamplePhotos,
  useSubmissions,
  useSubmitSubmission,
  useSupplierVisibleFiles,
  useUploadSamplePhoto,
  useUpdateReviewComment,
  useVisibleReviews,
} from './hooks';
import { groupSubmissions, reviewErrorMessage } from './types';
import { PortalHandoffs, PortalSubmissions } from '../supplierExchange/PortalExchange';
import { InventoryPanel } from '../inventory/InventoryPanel';
import { SupplierOwnTimings } from '../supplierExchange/SupplierOwnTimings';
import { useEffectiveSupplierOrg, useMimic } from '../supplierOrganizations/MimicMode';
import { PortalOrders } from '../supplierExchange/PortalOrders';
import type { BadgeTone } from '../../components/ui';
import type { SupplierReview, SupplierReviewStatus } from './types';

/**
 * The supplier portal (handoff 9, 32-37): everything a supplier account can
 * do, and nothing else. The section 9 navigation — Action Required, Shared
 * Reviews, Files for Review, Messages, Completed — lives as tabs inside the
 * one supplier route, matching the restrained shell.
 *
 * Row Level Security decides every list on this page: the queries are plain
 * reads, and a supplier sees exactly the reviews they were selected for
 * (proved by the Stage 1 staging assertions).
 */

/**
 * The eight ORIGINAL sections. No longer tabs — they are the panels the three
 * consolidated tabs are built from, and each still renders exactly as it did.
 * Regrouping them rather than rewriting them is deliberate: the consolidation
 * is a navigation change, and rewriting eight working panels at the same time
 * would make any regression impossible to attribute.
 */
type PortalSection =
  | 'action'
  | 'orders'
  | 'submit'
  | 'fromtap'
  | 'reviews'
  | 'files'
  | 'messages'
  | 'completed'
  | 'inventory'
  | 'timings';

/**
 * Which sections each tab shows, in order.
 *
 * Jobs is the board and NOTHING else. Everything a factory used to have to
 * scroll past — shared reviews, files, sample runs, completed work — either
 * moved onto the job it belongs to or moved to Messages. A tab that stacks
 * four unrelated lists is the shape this rebuild exists to remove, and putting
 * one back "just for now" is how it grows again.
 *
 * Sample Submissions is NOT here, and that is the second correction. Kyu asked
 * "do we need it? all files stay in an order til the end forever, right" —
 * and he is right: a sample run belongs to the job it was made for, and the
 * job order page already lists them. A standalone feed of every run the
 * factory has ever sent is a second copy of the same rows, sorted by date
 * instead of by job, and it was the longest thing on the tab.
 *
 * `fromtap` survives as an EXCEPTION section: design handoffs that have not
 * been acknowledged and have nowhere else to live yet. It renders only when
 * non-empty, so the ordinary factory sees a board and nothing under it.
 */
const TAB_SECTIONS: Record<PortalTab, PortalSection[]> = {
  jobs: ['orders', 'fromtap', 'timings'],
  inventory: ['inventory', 'submit'],
  messages: ['reviews', 'files'],
};

const SECTION_HEADINGS: Record<PortalSection, string> = {
  action: 'Needs your attention',
  orders: '',
  // Empty on purpose. PortalSubmissions prints its own "Sample submissions"
  // heading beside the New submission button, so a section heading here made
  // two headings for one thing — and while the stock panel was duplicated
  // below it, this one sat over nothing at all.
  submit: '',
  fromtap: 'Design handoffs awaiting your acknowledgement',
  reviews: 'Conversations',
  files: 'Files TAP has shared',
  messages: 'Messages',
  completed: 'Completed',
  inventory: '',
  timings: 'Your timings',
};

/** Sections that draw nothing at all when they have nothing to show. */
const HIDE_WHEN_EMPTY = new Set<PortalSection>(['fromtap']);

/** Statuses that put the ball in the supplier's court. */
const ACTION_KEYS = new Set(['awaiting_supplier', 'revision_requested', 'sample_in_progress']);

const STATUS_TONES: Record<string, BadgeTone> = {
  awaiting_supplier: 'brand',
  sample_in_progress: 'brand',
  ready_for_tap_review: 'warning',
  revision_requested: 'danger',
  approved_for_production: 'success',
  on_hold: 'neutral',
};

function statusOf(
  statuses: SupplierReviewStatus[],
  review: SupplierReview,
): SupplierReviewStatus | undefined {
  return statuses.find((status) => status.id === review.status_id);
}

/** One review opened full-screen: brief, samples, conversation. */
function ReviewDetail({
  review,
  statuses,
  onClose,
}: {
  review: SupplierReview;
  statuses: SupplierReviewStatus[];
  onClose: () => void;
}) {
  const pasteImage = usePasteImageUpload();
  const [error, setError] = useState('');
  const [newVersionNote, setNewVersionNote] = useState('');
  const [startingVersion, setStartingVersion] = useState(false);
  const [photoLabel, setPhotoLabel] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const [confirmingSubmit, setConfirmingSubmit] = useState<string | null>(null);
  const [commentBody, setCommentBody] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');
  // A pasted screenshot still uploading: Send and Save wait for it (design note S16).
  const [uploading, setUploading] = useState(false);
  const [editUploading, setEditUploading] = useState(false);

  const { access } = useAuth();
  const myId = access.status === 'approved' ? access.profile.id : '';

  const people = useReviewPeople(review.id);
  const submissions = useSubmissions(review.id);
  const photos = useSamplePhotos(review.id);
  const comments = useReviewComments(review.id);
  const sharedFiles = useSupplierVisibleFiles();

  const createSubmission = useCreateSubmission(review.id);
  const addPhoto = useAddSamplePhoto(review.id);
  const uploadPhoto = useUploadSamplePhoto(review.id);
  const submitSubmission = useSubmitSubmission(review.id, review.task_id);
  const createComment = useCreateReviewComment(review.id, review.project_id);
  const updateComment = useUpdateReviewComment(review.id);
  const deleteComment = useDeleteReviewComment(review.id);

  const status = statusOf(statuses, review);
  const peopleList = people.data ?? [];
  const versions = groupSubmissions(submissions.data ?? [], photos.data ?? []);
  const reviewFiles = (sharedFiles.data ?? []).filter((file) => file.task_id === review.task_id);
  const hasDraft = versions.some(({ submission }) => !submission.is_submitted);

  function personLabel(userId: string): string {
    const person = peopleList.find((row) => row.user_id === userId);
    if (!person) {
      return 'Someone';
    }
    return person.display_name.trim() !== '' ? person.display_name : person.email;
  }

  function report(cause: unknown) {
    setError(reviewErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function startVersion() {
    setError('');
    createSubmission.mutate(newVersionNote, {
      onSuccess: () => {
        setNewVersionNote('');
        setStartingVersion(false);
      },
      onError: report,
    });
  }

  function onPhotoPicked(submissionId: string, picked: File | undefined) {
    if (!picked) {
      return;
    }
    setError('');
    uploadPhoto.mutate(
      { submissionId, label: photoLabel, file: picked },
      {
        onSuccess: () => setPhotoLabel(''),
        onError: report,
      },
    );
  }

  function addPhotoToDraft(submissionId: string) {
    const url = photoUrl.trim();
    if (url === '') {
      setError('Paste a link to the photo first.');
      return;
    }
    setError('');
    addPhoto.mutate(
      {
        submissionId,
        label: photoLabel,
        name: url.split('/').pop() ?? 'photo',
        driveFileId: null,
        url,
        mimeType: null,
        sizeBytes: null,
      },
      {
        onSuccess: () => {
          setPhotoLabel('');
          setPhotoUrl('');
        },
        onError: report,
      },
    );
  }

  function postComment() {
    if (uploading) {
      return;
    }
    if (isRichTextEmpty(commentBody)) {
      setError('Type a message first.');
      return;
    }
    setError('');
    createComment.mutate(commentBody, { onSuccess: () => setCommentBody(''), onError: report });
  }

  function saveCommentEdit() {
    if (editUploading || editingCommentId === null || isRichTextEmpty(editingBody)) {
      return;
    }
    setError('');
    updateComment.mutate(
      { commentId: editingCommentId, body: editingBody },
      { onSuccess: () => setEditingCommentId(null), onError: report },
    );
  }

  return (
    <Dialog open title={review.shared_title} onClose={onClose} wide>
      {error === '' ? null : <InlineError>{error}</InlineError>}

      <div className="review-facts">
        <Badge tone={(status?.system_key && STATUS_TONES[status.system_key]) || 'neutral'}>
          {status?.name ?? 'Unknown'}
        </Badge>
        <span className="review-fact">
          Due {review.review_due_date ? formatDate(review.review_due_date) : '—'}
        </span>
        {review.approved_at ? (
          <span className="review-fact">Approved {formatDate(review.approved_at)}</span>
        ) : null}
      </div>

      {review.instructions === '' ? null : (
        <>
          <h4 className="review-subheading">Instructions from TAP</h4>
          <p className="task-panel-text">{review.instructions}</p>
        </>
      )}

      <h4 className="review-subheading">Design files shared with you</h4>
      {sharedFiles.isPending ? (
        <Skeleton lines={2} label="Loading shared files" />
      ) : reviewFiles.length === 0 ? (
        <p className="task-panel-text">No files shared yet.</p>
      ) : (
        <ul className="review-list">
          {reviewFiles.map((file) => (
            <li key={file.id}>
              <a href={file.url} target="_blank" rel="noreferrer" className="file-name">
                {file.name}
              </a>
            </li>
          ))}
        </ul>
      )}

      <h4 className="review-subheading">Sample versions</h4>
      {versions.length === 0 ? (
        <p className="task-panel-text">No sample versions yet. Start one when you are ready.</p>
      ) : (
        <ul className="review-list">
          {versions.map(({ submission, photos: versionPhotos }) => (
            <li key={submission.id} className="review-sample">
              <div className="review-row">
                <strong>
                  Sample V{submission.version_number}
                  {submission.is_submitted ? '' : ' (draft)'}
                </strong>
                <span className="review-fact">
                  {submission.is_submitted
                    ? `Submitted ${formatDate(submission.submitted_at)}`
                    : 'Not submitted yet'}
                </span>
              </div>
              {submission.note === '' ? null : <p className="task-panel-text">{submission.note}</p>}
              {versionPhotos.length === 0 ? null : (
                <ul className="review-photo-list">
                  {versionPhotos.map((photo) => (
                    <li key={photo.id}>
                      <a href={photo.url} target="_blank" rel="noreferrer" className="file-name">
                        {photo.label !== '' ? `${photo.label} — ${photo.name}` : photo.name}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              {submission.is_submitted ? null : (
                <div className="review-composer">
                  <div className="review-row">
                    <Input
                      label="Photo label"
                      value={photoLabel}
                      placeholder="Front, Side, Packaging…"
                      onChange={(event) => setPhotoLabel(event.target.value)}
                    />
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="file-input-hidden"
                      aria-label="Choose a photo to upload"
                      onChange={(event) => {
                        onPhotoPicked(submission.id, event.target.files?.[0]);
                        // Allow picking the same file again after a failure.
                        if (photoInputRef.current) {
                          photoInputRef.current.value = '';
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadPhoto.isPending}
                    >
                      {uploadPhoto.isPending ? 'Uploading…' : 'Upload photo'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setLinkOpen((value) => !value)}
                    >
                      {linkOpen ? 'Cancel link' : 'Paste a link instead'}
                    </Button>
                  </div>
                  {linkOpen ? (
                    <div className="review-row">
                      <Input
                        label="Photo link (https)"
                        value={photoUrl}
                        placeholder="https://…"
                        onChange={(event) => setPhotoUrl(event.target.value)}
                      />
                      <Button
                        size="sm"
                        onClick={() => addPhotoToDraft(submission.id)}
                        disabled={addPhoto.isPending}
                      >
                        {addPhoto.isPending ? 'Adding…' : 'Add photo'}
                      </Button>
                    </div>
                  ) : null}
                  {confirmingSubmit === submission.id ? (
                    <span className="review-actions">
                      <span className="task-panel-text">
                        Submitting locks this version and notifies TAP.
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmingSubmit(null)}>
                        Keep editing
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setConfirmingSubmit(null);
                          setError('');
                          submitSubmission.mutate(submission.id, { onError: report });
                        }}
                      >
                        Confirm submit
                      </Button>
                    </span>
                  ) : (
                    <Button size="sm" onClick={() => setConfirmingSubmit(submission.id)}>
                      Submit for TAP Review
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {hasDraft ? null : startingVersion ? (
        <div className="review-composer">
          <Textarea
            label="Version note (optional)"
            value={newVersionNote}
            rows={2}
            onChange={(event) => setNewVersionNote(event.target.value)}
          />
          <span className="review-actions">
            <Button size="sm" onClick={startVersion} disabled={createSubmission.isPending}>
              {createSubmission.isPending ? 'Starting…' : 'Start version'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setStartingVersion(false)}>
              Cancel
            </Button>
          </span>
        </div>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => setStartingVersion(true)}>
          Start a new sample version
        </Button>
      )}

      <h4 className="review-subheading">Conversation with TAP</h4>
      {(comments.data ?? []).length === 0 ? (
        <p className="task-panel-text">No messages yet.</p>
      ) : (
        <ul className="review-list">
          {(comments.data ?? []).map((comment) => (
            <li key={comment.id} className="review-comment">
              <div className="review-row">
                <strong>{personLabel(comment.author_id)}</strong>
                <span className="review-fact">
                  {formatDate(comment.created_at)}
                  {comment.edited_at ? ' · edited' : ''}
                </span>
              </div>
              {editingCommentId === comment.id ? (
                <>
                  <RichTextEditor
                    label="Edit message"
                    value={editingBody}
                    onChange={setEditingBody}
                    onPasteImage={pasteImage}
                    onUploadingChange={setEditUploading}
                  />
                  <span className="review-actions">
                    <Button size="sm" onClick={saveCommentEdit} disabled={editUploading}>
                      Save
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditingCommentId(null)}>
                      Cancel
                    </Button>
                  </span>
                </>
              ) : (
                <RichTextView value={comment.body} />
              )}
              {comment.author_id === myId && editingCommentId !== comment.id ? (
                <span className="review-actions">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Edit message from ${formatDate(comment.created_at)}`}
                    onClick={() => {
                      setEditingCommentId(comment.id);
                      setEditingBody(comment.body);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Delete message from ${formatDate(comment.created_at)}`}
                    onClick={() => deleteComment.mutate(comment.id, { onError: report })}
                  >
                    Delete
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <div className="review-composer">
        <RichTextEditor
          label="Message TAP"
          value={commentBody}
          onChange={setCommentBody}
          onPasteImage={pasteImage}
          onUploadingChange={setUploading}
        />
        <Button size="sm" onClick={postComment} disabled={createComment.isPending || uploading}>
          {createComment.isPending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </Dialog>
  );
}

export function SupplierReviewPortal() {
  // The tab comes from the URL so a link can point at one, which is what the
  // Inbox notifications and TAP's own emails need. Old ids redirect rather
  // than 404 — see portalTabs.
  const [params, setParams] = useSearchParams();
  const tab = tabFromParams(params);
  function setTab(next: PortalTab) {
    const updated = new URLSearchParams(params);
    updated.set('tab', next);
    setParams(updated, { replace: true });
  }
  const [openReviewId, setOpenReviewId] = useState<string | null>(null);

  const organizationId = useEffectiveSupplierOrg();
  const { canWrite } = useMimic();
  const reviews = useVisibleReviews();
  const statuses = useReviewStatuses();
  const sharedFiles = useSupplierVisibleFiles();
  const exchangeSubmissions = useExchangeSubmissions();
  const handoffs = useHandoffs();
  const revisionsNeeded = (exchangeSubmissions.data ?? []).filter(
    (submission) => submission.status === 'revision_requested',
  );
  const unacknowledged = (handoffs.data ?? []).filter(
    (handoff) => handoff.acknowledged_at === null,
  );
  const approvedRuns = completedSubmissions(exchangeSubmissions.data ?? []);

  const statusList = useMemo(() => statuses.data ?? [], [statuses.data]);
  const reviewList = useMemo(() => reviews.data ?? [], [reviews.data]);
  const closeDetail = useCallback(() => setOpenReviewId(null), []);

  const byTab: Record<'action' | 'reviews' | 'completed', SupplierReview[]> = useMemo(() => {
    const withKey = reviewList.map((review) => ({
      review,
      key: statusOf(statusList, review)?.system_key ?? '',
    }));
    return {
      action: withKey.filter(({ key }) => ACTION_KEYS.has(key)).map(({ review }) => review),
      reviews: reviewList,
      completed: withKey
        .filter(({ key }) => key === 'approved_for_production')
        .map(({ review }) => review),
    };
  }, [reviewList, statusList]);

  const actionCount = byTab.action.length + revisionsNeeded.length + unacknowledged.length;

  /**
   * Sections with nothing in them, so the exception panels can disappear.
   *
   * Computed here rather than inside each panel because the panel is what gets
   * hidden — a component that renders nothing still leaves its heading and its
   * spacing behind, which is exactly the empty-box-under-a-board look this
   * rebuild is removing.
   */
  const emptySections = new Set<PortalSection>([
    ...(unacknowledged.length === 0 ? (['fromtap'] as const) : []),
  ]);

  const openReview = reviewList.find((review) => review.id === openReviewId);

  if (reviews.isPending || statuses.isPending) {
    return <Skeleton lines={3} label="Loading your reviews" />;
  }
  if (reviews.isError) {
    return (
      <EmptyState
        title="Your reviews could not be loaded"
        description="Something went wrong. Try again in a moment."
      />
    );
  }

  function reviewRows(rows: SupplierReview[], emptyTitle: string) {
    if (rows.length === 0) {
      return <EmptyState title={emptyTitle} />;
    }
    // One grouped row per review: what it is, when it is due, where it
    // stands, and the whole row opens it. The same row a TAP admin sees on
    // Settings and Admin, so the two sides of the portal stop looking like two
    // products.
    return (
      <RowGroup>
        {rows.map((review) => {
          const status = statusOf(statusList, review);
          return (
            <Row
              key={review.id}
              icon="reviews"
              label={review.shared_title}
              note={`Due ${review.review_due_date ? formatDate(review.review_due_date) : '—'}`}
              value={
                <Badge tone={(status?.system_key && STATUS_TONES[status.system_key]) || 'neutral'}>
                  {status?.name ?? 'Unknown'}
                </Badge>
              }
              onClick={() => setOpenReviewId(review.id)}
            />
          );
        })}
      </RowGroup>
    );
  }

  function body(section: PortalSection) {
    switch (section) {
      case 'submit':
        return <PortalSubmissions />;
      case 'fromtap':
        return <PortalHandoffs />;
      case 'action': {
        if (actionCount === 0) {
          return <EmptyState title="Nothing needs your action right now" />;
        }
        return (
          <>
            {revisionsNeeded.length > 0 ? (
              <RowGroup title="Revise and resubmit">
                {revisionsNeeded.map((submission) => (
                  <Row
                    key={submission.id}
                    icon="orders"
                    label={submission.title}
                    note="Revise and resubmit under Send Samples"
                    value={<Badge tone="danger">Revision requested</Badge>}
                    onClick={() => setTab('jobs')}
                  />
                ))}
              </RowGroup>
            ) : null}
            {unacknowledged.length > 0 ? (
              <RowGroup title="Acknowledge">
                {unacknowledged.map((handoff) => (
                  <Row
                    key={handoff.id}
                    icon="inbox"
                    label={handoff.title}
                    note="See From TAP below to acknowledge"
                    value={<Badge tone="brand">Awaiting your acknowledgement</Badge>}
                    onClick={() => setTab('jobs')}
                  />
                ))}
              </RowGroup>
            ) : null}
            {byTab.action.length > 0 ? reviewRows(byTab.action, '') : null}
          </>
        );
      }
      case 'orders':
        return <PortalOrders />;
      case 'reviews':
        return reviewRows(byTab.reviews, 'No reviews shared with you yet');
      case 'completed':
        // Approved sample runs land here rather than staying in Send Samples.
        // They are finished and cannot change, and leaving them in the active
        // feed buried the runs that still needed something.
        return (
          <div>
            {approvedRuns.length === 0 ? null : (
              <RowGroup title="Approved sample runs">
                {approvedRuns.map((submission) => (
                  <Row
                    key={submission.id}
                    icon="orders"
                    label={submission.title}
                    note={`Decided ${
                      submission.decided_at === null
                        ? formatDate(submission.created_at)
                        : formatDate(submission.decided_at)
                    }${submission.decision_note === '' ? '' : ` · TAP: ${submission.decision_note}`}`}
                    value={<Badge tone="success">Approved for production</Badge>}
                  />
                ))}
              </RowGroup>
            )}
            {/* The empty state belongs to the whole tab, not to the review
                list alone: "No completed reviews yet" under a list of six
                approved runs reads as a fault. */}
            {byTab.completed.length === 0 && approvedRuns.length > 0
              ? null
              : reviewRows(byTab.completed, 'Nothing completed yet')}
          </div>
        );
      case 'files': {
        const files = sharedFiles.data ?? [];
        if (files.length === 0) {
          return <EmptyState title="No files shared with you yet" />;
        }
        // A real link, not a row with an onClick: a file opens in a new tab,
        // and a link keeps right-click, middle-click and copy-address working.
        return (
          <RowGroup>
            {files.map((file) => (
              <Row
                key={file.id}
                icon="files"
                label={
                  <a href={file.url} target="_blank" rel="noreferrer" className="file-name">
                    {file.name}
                  </a>
                }
                note={`Shared ${formatDate(file.created_at)}`}
              />
            ))}
          </RowGroup>
        );
      }
      case 'timings':
        // Their own numbers, on their own screen. A measurement kept on TAP's
        // side creates no pressure at all — it only ever arrives as a
        // complaint. Their own figures they can see, argue with and improve.
        //
        // Their own, never the comparison across factories: that is a separate
        // database function, not a filter applied here.
        return <SupplierOwnTimings organizationId={organizationId} />;
      case 'inventory':
        // The factory's own stock. It existed the whole time and was only ever
        // rendered on TAP's screen, so the people who actually count the beads
        // could not see or update the count. audience='supplier' is what stops
        // TAP's copy ("so TAP can see your levels") reading as instructions to
        // somebody who IS the supplier.
        return (
          <InventoryPanel
            supplierOrganizationId={organizationId}
            canUpload={canWrite}
            audience="supplier"
          />
        );
      case 'messages':
        return (
          <>
            <p className="task-panel-text">
              Conversations live inside each review. Open one to read and reply.
            </p>
            {reviewRows(byTab.reviews, 'No reviews shared with you yet')}
          </>
        );
    }
  }

  return (
    <div className="supplier-portal">
      <div className="inbox-filter-row" role="group" aria-label="Portal sections">
        {PORTAL_TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={
              entry.id === tab ? 'saved-view-chip saved-view-chip--active' : 'saved-view-chip'
            }
            aria-pressed={entry.id === tab}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
            {/* The count follows the work, which is now the board. A badge on
                a tab nobody is looking at is a badge that stops meaning
                anything. */}
            {entry.id === 'jobs' && actionCount > 0 ? ` (${actionCount})` : ''}
          </button>
        ))}
      </div>

      {TAB_SECTIONS[tab].map((key) => {
        if (HIDE_WHEN_EMPTY.has(key) && emptySections.has(key)) {
          return null;
        }
        const heading = SECTION_HEADINGS[key];
        return (
          <section
            key={key}
            className="supplier-portal-section"
            aria-label={heading === '' ? key : heading}
          >
            {/* A heading only where one section sits beside another. The board
                is the whole tab and had "YOUR ORDERS" written over it, which
                is a label on a thing that needs no label. */}
            {heading === '' ? null : <h3 className="project-category-heading">{heading}</h3>}
            {body(key)}
          </section>
        );
      })}

      {openReview ? (
        <ReviewDetail review={openReview} statuses={statusList} onClose={closeDetail} />
      ) : null}
    </div>
  );
}
